// src/fileUtils/cleanFileUtils.js - Enhanced to ensure complete metadata preservation

/**
 * Cleans file objects for storage by removing binary data while properly preserving ALL metadata
 * 
 * @param {Array} files - Array of file objects from uploads
 * @returns {Array} - Array of cleaned file objects safe for storage
 */
export const cleanFilesForStorage = (files) => {
  if (!files || !Array.isArray(files)) return [];
  
  return files.map(file => {
    // Start with a base object containing essential metadata
    const cleanedFile = {
      // CRITICAL: Ensure original filename is preserved as the primary property
      name: file.name || file.originalName || file.fileName || 'Unknown file',
      
      // MIME type and size
      type: file.type || file.mimeType || '',
      size: file.size || 0,
      
      // Store timestamp of when the file was processed
      processedAt: new Date().toISOString(),
    };
    
    // Preserve ALL possible metadata fields that might be useful for display
    // Image dimensions
    if (file.width !== undefined) cleanedFile.width = file.width;
    if (file.height !== undefined) cleanedFile.height = file.height;
    
    // Document metadata
    if (file.pageCount !== undefined) cleanedFile.pageCount = file.pageCount;
    if (file.wordCount !== undefined) cleanedFile.wordCount = file.wordCount;
    if (file.characterCount !== undefined) cleanedFile.characterCount = file.characterCount;
    
    // Spreadsheet metadata
    if (file.rowCount !== undefined) cleanedFile.rowCount = file.rowCount;
    if (file.columnCount !== undefined) cleanedFile.columnCount = file.columnCount;
    if (file.sheetCount !== undefined) cleanedFile.sheetCount = file.sheetCount;
    
    // Content type flags - these are useful for displaying appropriate icons and handling
    cleanedFile.isImage = !!file.isImage || file.type?.startsWith('image/') || file.name?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i) || false;
    cleanedFile.isPDF = !!file.isPDF || file.type === 'application/pdf' || file.name?.endsWith('.pdf') || false;
    cleanedFile.isCSV = !!file.isCSV || file.type === 'text/csv' || file.name?.endsWith('.csv') || false;
    cleanedFile.isExcel = !!file.isExcel || file.type?.includes('spreadsheet') || file.type?.includes('excel') || file.name?.match(/\.(xlsx|xls|ods)$/i) || false;
    cleanedFile.isDocx = !!file.isDocx || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name?.endsWith('.docx') || false;
    cleanedFile.isText = !!file.isText || file.type?.startsWith('text/') || file.name?.match(/\.(txt|md|js|py|java|cpp|html|css|json)$/i) || false;
    
    // Original creation/modification date if available
    if (file.lastModified) cleanedFile.lastModified = file.lastModified;
    if (file.lastModifiedDate) cleanedFile.lastModifiedDate = file.lastModifiedDate;
    
    // Any custom metadata properties that might be useful
    if (file.description) cleanedFile.description = file.description;
    if (file.title) cleanedFile.title = file.title;
    if (file.author) cleanedFile.author = file.author;
    if (file.keywords) cleanedFile.keywords = file.keywords;
    
    // We specifically DO NOT include these binary/content properties:
    // - dataUrl (binary data encoded as base64)
    // - buffer (ArrayBuffer with raw binary data)
    // - content (file content, often large)
    // - text (for text files, can be very large)
    // - html (parsed HTML content)
    // - parsed (parsed content like JSON/CSV data)
    
    // Return the cleaned file with all metadata preserved
    return cleanedFile;
  });
};

/**
 * Utility function to extract filename extension from MIME type
 * Used as a fallback when generating filenames
 * 
 * @param {string} mimeType - MIME type
 * @returns {string} - Corresponding file extension
 */
export const getExtensionFromMimeType = (mimeType) => {
  if (!mimeType) return '';
  
  // Comprehensive MIME type to extension mapping
  const mimeToExtension = {
    // Document formats
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/msword': 'doc',
    'application/rtf': 'rtf',
    'application/vnd.oasis.opendocument.text': 'odt',
    
    // Spreadsheet formats
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.oasis.opendocument.spreadsheet': 'ods',
    'text/csv': 'csv',
    'text/tab-separated-values': 'tsv',
    
    // Presentation formats
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.oasis.opendocument.presentation': 'odp',
    
    // Text formats
    'text/plain': 'txt',
    'text/markdown': 'md',
    'text/html': 'html',
    'text/css': 'css',
    'text/xml': 'xml',
    'application/json': 'json',
    'application/javascript': 'js',
    'text/javascript': 'js',
    
    // Image formats
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
    
    // Archive formats
    'application/zip': 'zip',
    'application/x-rar-compressed': 'rar',
    'application/gzip': 'gz',
    'application/x-tar': 'tar',
    
    // Programming formats
    'text/x-python': 'py',
    'text/x-java': 'java',
    'text/x-c++': 'cpp',
    'text/x-csharp': 'cs'
  };
  
  // Try to get extension from our mapping
  if (mimeToExtension[mimeType]) {
    return mimeToExtension[mimeType];
  }
  
  // If not found in mapping, try to extract from MIME type
  const parts = mimeType.split('/');
  if (parts.length === 2) {
    // For some types, use the subtype directly
    const subtype = parts[1];
    
    // Handle special cases with complex subtypes
    if (subtype.includes('vnd.')) {
      // Try to extract a meaningful part from vendor-specific types
      const match = subtype.match(/vnd\.([^.]+)/);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    // For others, return the subtype if it's simple enough
    if (subtype.length < 10 && !subtype.includes('+')) {
      return subtype;
    }
  }
  
  // Default fallback
  return 'bin';
};